import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, SectionHead, EASE } from "@/components/motion";

const FOCUS = [
  {
    title: "Hotel Investment & Feasibility",
    why: "Testing whether market, product and economics support an investment case.",
  },
  {
    title: "Hospitality Advisory",
    why: "Turning evidence into a defensible recommendation.",
  },
  {
    title: "Hotel Development",
    why: "Connecting concept, positioning and operating strategy.",
  },
  {
    title: "Revenue & Commercial Analytics",
    why: "Understanding how demand, pricing and profitability interact.",
  },
];

export default function About() {
  const [active, setActive] = useState(null);

  return (
    <section id="about" className="border-t border-hairline" data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-36">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead label="About" title={<>Hospitality from both sides of the <em className="text-moss">spreadsheet.</em></>} />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-16">
            <Reveal>
              <p className="text-base sm:text-lg leading-relaxed text-smoke">
                Operational hotel experience shaped how I understand service, demand and departmental performance. My MSc expanded that perspective into feasibility, investment, revenue management, brand strategy and commercial analysis.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 font-serif text-xl sm:text-2xl font-light italic leading-snug text-ink">
                My focus is connecting what happens on property with the decisions that shape hotel performance.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 sm:mt-28">
          <Reveal>
            <p className="overline-label mb-8">Current focus</p>
          </Reveal>
          <div className="grid grid-cols-1 border-l border-t border-hairline sm:grid-cols-2 lg:grid-cols-4">
            {FOCUS.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.07} y={20}>
                <button
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(active === i ? null : i)}
                  data-testid={`focus-tile-${i + 1}`}
                  aria-expanded={active === i}
                  className="group relative flex h-full min-h-[220px] w-full flex-col justify-between border-b border-r border-hairline p-7 text-left transition-colors duration-500 hover:bg-moss"
                >
                  <div className="flex items-start justify-between">
                    <span className="text-[11px] font-semibold tracking-[0.2em] text-fog transition-colors duration-500 group-hover:text-champagne">
                      0{i + 1}
                    </span>
                    <Plus
                      size={16}
                      strokeWidth={1.25}
                      className={`text-fog transition-[transform,color] duration-500 group-hover:text-champagne ${active === i ? "rotate-45" : ""}`}
                    />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-light leading-tight text-ink transition-colors duration-500 group-hover:text-paper">
                      {f.title}
                    </h3>
                    <AnimatePresence>
                      {active === i && (
                        <motion.p
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.45, ease: EASE }}
                          className="overflow-hidden text-sm leading-relaxed text-paper/70"
                          data-testid={`focus-tile-${i + 1}-why`}
                        >
                          <span className="block pt-3">{f.why}</span>
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
